import { useQuery } from '@tanstack/react-query';
import {
  ACTIVITY_METRICS_FEATURE_KEY,
  getPortalFeatures,
  type PortalFeatures,
} from '../api/portalFeatures';
import { useAuth } from '../auth/useAuth';

const PORTAL_FEATURES_QUERY_KEY = ['portal-features'] as const;

/**
 * Fetches the authenticated user's feature access map (default-deny grants managed
 * by Ops). Disabled while unauthenticated — the endpoint requires an External ID
 * Bearer token.
 */
export function usePortalFeatures() {
  const { isAuthenticated } = useAuth();
  return useQuery<PortalFeatures>({
    queryKey: PORTAL_FEATURES_QUERY_KEY,
    queryFn: getPortalFeatures,
    enabled: isAuthenticated,
  });
}

/**
 * Access state for the Activity Metrics feature. `hasAccess` is `false` while
 * unauthenticated, loading, or errored — default-deny, the entry point appears
 * only after the grant check resolves positively. UI visibility only; the
 * backend enforces the grant on every `/v1/activity/*` call.
 */
export function useActivityMetricsAccess(): {
  hasAccess: boolean;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = usePortalFeatures();
  return {
    hasAccess: data?.features[ACTIVITY_METRICS_FEATURE_KEY] === true,
    isLoading,
    error,
  };
}
